import React, { useEffect, useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { CLIENT_TYPES } from '@medflex/utils/constants';
import { pushCustomDimensions } from '@medflex/utils/Tracking';
import UserManagerSwitch from '@medflex/components/stories/UserManager';
import useUserSession from '@medflex/hooks/useUserSession';
import config from '@medflex/utils/config';
import { I18nextProvider } from 'react-i18next';
import { components } from '@medflex/i18n/lib/de';
import Logger from '@medflex/utils/Logger';
import NativeAppAutoRedirect from '@medflex/components/stories/NativeAppAutoRedirect';
import ErrorPage from './components/ErrorPage';
import PlayStoreBannerModule from './modules/PlayStoreBannerModule';
import useUserProfile from './hooks/useUserProfile';
import i18n from './i18n';
import physician from './i18n/resources/de/physician.json';
import physicianRegistration from './i18n/resources/de/physician_registration.json';
import patient from './i18n/resources/de/patient.json';
import patientRegistration from './i18n/resources/de/patient_registration.json';
import staff from './i18n/resources/de/staff.json';

const fetchTranslationsFromRemotes = async () => {
  return Promise.all([
    // eslint-disable-next-line import/no-unresolved
    import('RemoteInbox/translations'),
    // eslint-disable-next-line import/no-unresolved
    import('RemoteWidget/translations'),
    // eslint-disable-next-line import/no-unresolved
    import('RemoteActivities/translations'),
  ]);
};

const initTranslations = async i18n => {
  try {
    const remoteTranslations = await fetchTranslationsFromRemotes();

    remoteTranslations.forEach(t =>
      Object.entries(t).forEach(([lng, nsToResource]) =>
        Object.entries(nsToResource).forEach(([ns, resource]) =>
          i18n.addResourceBundle(lng, ns, resource, true, true),
        ),
      ),
    );
  } catch (e) {
    Logger.error('Error loading translations', e);
  }
};

const AppWrapper = ({
  children,
  baseName,
  oidcAuthority,
  clientId,
  postLogoutRedirectUri,
  desktopRedirectRoute,
  mobileRedirectRoute,
}) => {
  const { visibilityState } = useSelector(state => state.system);
  const { onSessionLoaded, session } = useUserSession();
  const userDimensionsSet = useRef(false);
  // eslint-disable-next-line no-underscore-dangle
  const { expired } = useSelector(state => state._chat);
  const { userProfile } = useUserProfile();
  const [isRedirectChecked, setRedirectChecked] = useState(false);

  const i18nInstance = useMemo(
    () =>
      i18n({
        resources: {
          de: { components, physician, physicianRegistration, patient, patientRegistration, staff },
        },
      }),
    [],
  );

  useEffect(() => {
    initTranslations(i18nInstance);
  }, [i18nInstance]);

  useEffect(() => {
    if (!userDimensionsSet.current && session) {
      const {
        profile: { email = '' },
      } = session;
      pushCustomDimensions({
        userType: session.userType,
        userId: session.profile.userId,
        clientVersion: config.VERSION,
        isEmployee: email.includes('@medflex').toString(),
      });
      userDimensionsSet.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, userDimensionsSet.current]);

  const shouldUseWebViewHandler =
    config.CLIENT_TYPE === CLIENT_TYPES.MOBILE || config.CLIENT_TYPE === CLIENT_TYPES.CHAT;

  const isSilentRenewDisabled =
    visibilityState === 'hidden' || config.CLIENT_TYPE === CLIENT_TYPES.MOBILE;

  return (
    <I18nextProvider i18n={i18nInstance}>
      <ErrorPage userType={userProfile?.type}>
        <BrowserRouter basename={baseName || config.BASE_PATH}>
          {isRedirectChecked ? (
            <UserManagerSwitch
              oidcAuthority={oidcAuthority || config.OIDC_AUTHORITY}
              clientId={clientId || config.OIDC_CLIENT_ID}
              postLogoutRedirectUri={postLogoutRedirectUri || config.OIDC_POST_LOGOUT_REDIRECT_URL}
              basePath={baseName || config.BASE_PATH}
              onSessionLoaded={onSessionLoaded}
              silentRenewDisabled={isSilentRenewDisabled}
              externalExpired={expired}
              shouldUseWebViewHandler={shouldUseWebViewHandler}
            >
              <PlayStoreBannerModule />
              {session && children}
            </UserManagerSwitch>
          ) : (
            <NativeAppAutoRedirect
              setRedirectChecked={setRedirectChecked}
              desktopRedirectRoute={desktopRedirectRoute}
              mobileRedirectRoute={mobileRedirectRoute}
            />
          )}
        </BrowserRouter>
      </ErrorPage>
    </I18nextProvider>
  );
};

AppWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  baseName: PropTypes.string,
  desktopRedirectRoute: PropTypes.string,
  mobileRedirectRoute: PropTypes.string,
  oidcAuthority: PropTypes.string,
  clientId: PropTypes.string,
  postLogoutRedirectUri: PropTypes.string,
};

export default AppWrapper;
